import userSettings from "../user_settings"
import {loggedInUser, onDashboard} from "./helpers"
import routeRegex from "../route_regex"
import AlephBet from "alephbet"
import signals from "../signals"

export const registrationPageVisited =
  new AlephBet.Goal("registration page visited")

export const registeredSuccessfully =
  new AlephBet.Goal("registered successfully")

export const premiumSubscriptionStarted =
  new AlephBet.Goal("premium subscription started")

export const embeddedQuizClicked =
  new AlephBet.Goal("Embedded quiz was clicked")

export const inlineQuizFinished =
  new AlephBet.Goal("3 questions complete inline quiz")

export const checkoutPageVisited = new AlephBet.Goal("checkout page visited")

export const lifetimeCheckoutPageVisited =
  new AlephBet.Goal("lifetime checkout page visited")

export const contentAccessed = new AlephBet.Goal("content accessed")

export const contentAccessedFive =
  new AlephBet.Goal("content accessed 5 or more times")

export const successStoriesPageVisited =
  new AlephBet.Goal("success stories visited")

export const searchPageVisited = new AlephBet.Goal("search page visited")

export const pricingPageVisited = new AlephBet.Goal("pricing page visited")

const pricingPageVisitedByRegisteredUser =
  new AlephBet.Goal("pricing page visited by registered user")

export const newWebSession =
  new AlephBet.Goal("web sessions", {unique: false})

export const paymentInitiated = new AlephBet.Goal("payment initiated")

export const hostedPaymentInitiated =
  new AlephBet.Goal("hosted payment initiated")

export const cardPaymentInitiated =
  new AlephBet.Goal("card payment initiated")

export const quizStarted = new AlephBet.Goal("quiz started")

export const quizOrVideoStarted = new AlephBet.Goal("quiz or video started")

export const quizStartedNonUnique =
  new AlephBet.Goal("quiz started non-unique", {unique: false})

export const customQuizViewed = new AlephBet.Goal("custom quiz viewed")

export const customQuizStarted = new AlephBet.Goal("custom quiz started")

export const videoPlayed = new AlephBet.Goal("video played")

export const notOnDashboard = new AlephBet.Goal("outside Dashboard")

export const setupCompleted = new AlephBet.Goal("setup completed")

export const studyUnitVisited = new AlephBet.Goal("study unit visited")

// when we run more than one experiment on the same page
// or even on different pages where we might want to check
// for interaction effects, then we want to create goals that
// show us not only their completion, but also which *other*
// experiment variant was picked.
//
// This method allows us to pass any number of experiments we
// want to tag this way
export function completeCrossExperimentGoal(experiments, goal) {
  for (const experiment of experiments) {
    // for every  *other* experiment in the list
    for (const otherExperiment of experiments) {
      if (otherExperiment.name !== experiment.name) {
        const otherName = otherExperiment.name
        const otherVariant = otherExperiment.getStoredVariant()
        const goalName = `${goal.name} [${otherName}=${otherVariant}]`
        // we complete a new goal in the format:
        // "goal [other_experiment={variant}]"
        experiment.goal_complete(goalName, goal.props)
      }
    }
  }
}

export function checkGoals() {
  const gb = window.gb
  if (window.gon.registeredWithProvider) {
    registeredSuccessfully.complete()
    gb?.logEvent("registration success")
  }

  signals.on("kenhub.subscription_started", () => {
    premiumSubscriptionStarted.complete()
    gb?.logEvent("subscription started")
  })

  signals.on("kenhub.embeddedQuizClicked", () => {
    embeddedQuizClicked.complete()
    gb?.logEvent("embedded quiz clicked")
  })

  signals.on("kenhub.inlineQuizFinished", () => {
    inlineQuizFinished.complete()
    gb?.logEvent("inline quiz finished")
  })

  signals.on("kenhub.subscription_started", () => {
    premiumSubscriptionStarted.complete()
    gb?.logEvent("subscription started")
  })

  if (window.location.pathname.match(routeRegex("/success-stories"))) {
    successStoriesPageVisited.complete()
    gb?.logEvent("success stories page viewed")
  }

  if (window.location.pathname.match(routeRegex("/search"))) {
    searchPageVisited.complete()
    gb?.logEvent("search page viewed")
  }

  if (window.location.pathname.match(/pricing$/)) {
    pricingPageVisited.complete()
    gb?.logEvent("pricing page viewed")
    if (loggedInUser()) {
      pricingPageVisitedByRegisteredUser.complete()
      gb?.logEvent("pricing page viewed by registered user")
    }
  }

  if (window.location.pathname.match(/checkout$/)) {
    checkoutPageVisited.complete()
    gb?.logEvent("checkout page viewed")
  }

  if (
    window.location.pathname.match(/checkout$/) &&
    window.location.search.includes("plan=lifetime")
  ) {
    lifetimeCheckoutPageVisited.complete()
    gb?.logEvent("lifetime checkout page viewed")
  }

  if (
    window.location.pathname.match(
      routeRegex("/(register|registration-ebook|registration-email)$")
    )
  ) {
    registrationPageVisited.complete()
    gb?.logEvent("registration page viewed")
  }

  signals.on("kenhub.new_web_session", () => {
    newWebSession.complete()
    gb?.logEvent("new web session")
  })

  signals.on("kenhub.payment_initiated", ({type}) => {
    if (type === "card") {
      cardPaymentInitiated.complete()
    } else {
      hostedPaymentInitiated.complete()
    }
    paymentInitiated.complete()
    gb?.logEvent("payment initiated", {type})
  })

  signals.on("kenhub.quiz_started", () => {
    quizStarted.complete()
    quizStartedNonUnique.complete()
    quizOrVideoStarted.complete()
    incrementContentAccessed()
    contentAccessed.complete()
    gb?.logEvent("quiz started")
  })

  signals.on("kenhub.video_played", () => {
    videoPlayed.complete()
    quizOrVideoStarted.complete()
    incrementContentAccessed()
    contentAccessed.complete()
    gb?.logEvent("video_played")
  })

  signals.on("kenhub.custom_quiz_started", () => {
    customQuizStarted.complete()
    gb?.logEvent("custom quiz started")
  })

  if (
    ["article read", "atlas term viewed"].includes(
      window.gon.tracking?.pageView
    )
  ) {
    incrementContentAccessed()
    contentAccessed.complete()
    gb?.logEvent("content accessed")
  }

  if (window.gon.tracking?.pageView === "custom quiz page viewed") {
    customQuizViewed.complete()
    gb?.logEvent("custom quiz viewed")
  }

  if (contentAccessedCount() >= 5) contentAccessedFive.complete()

  if (!onDashboard()) notOnDashboard.complete()

  if (window.gon.currentUser && window.gon.currentUser.fieldOfStudy) {
    setupCompleted.complete()
  }

  if (/\/study\//.test(window.location.pathname)) {
    studyUnitVisited.complete()
    gb?.logEvent("study unit viewed")
  }
}

function incrementContentAccessed() {
  const settings = userSettings.get("experiments") || {}
  settings.contentAccessedCount = contentAccessedCount() + 1
  userSettings.register({experiments: settings})
}

function contentAccessedCount() {
  const settings = userSettings.get("experiments") || {}
  return settings.contentAccessedCount || 0
}
